/**
 * Platform (super-admin) auth client — PARALLEL to the tenant auth layer.
 *
 * Deliberately separate from the tenant session (apps/web/src/auth/tokenStore.ts
 * + apps/web/src/api/client.ts). The two never share state: a tenant login and
 * an operator login can coexist in the same browser without clobbering each
 * other, and the enforcement boundary ("platform routes never import tenant
 * auth, tenant routes never import this") stays crisp.
 *
 * Mirrors the tenant pattern structurally:
 *   - access token in memory only (never localStorage — XSS stance)
 *   - refresh token is the httpOnly `__sa_refresh` cookie (set by the API,
 *     invisible to JS), replayed automatically via credentials:'include'
 *   - on 401, one silent refresh against /v1/platform/auth/refresh + retry
 *   - concurrent 401s coalesce into a single in-flight refresh
 *
 * NB: the tenant client lives at api/client.ts (NOT lib/auth.ts, which the
 * Phase B prompt assumed and which does not exist). We honour the locked
 * target path (lib/platform-auth.ts) while mirroring the *actual* tenant
 * shape — including its `ApiRequest` options object rather than a raw
 * RequestInit. Flagged in the PR judgment calls.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

const PLATFORM_REFRESH_PATH = '/v1/platform/auth/refresh';

// ---------------------------------------------------------------------------
// Error type (local — keeps this module independent of the tenant client)
// ---------------------------------------------------------------------------

export class PlatformApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'PlatformApiError';
    this.status = status;
    this.body = body;
  }
}

// ---------------------------------------------------------------------------
// In-memory token store
// ---------------------------------------------------------------------------

let superAdminAccessToken: string | null = null;

export const superAdminTokenStore = {
  get: (): string | null => superAdminAccessToken,
  set: (token: string | null): void => {
    superAdminAccessToken = token;
  },
  clear: (): void => {
    superAdminAccessToken = null;
  },
};

/** Imperative check used by the platform route guards. */
export function isPlatformAuthenticated(): boolean {
  return superAdminAccessToken !== null;
}

// ---------------------------------------------------------------------------
// Request wrapper
// ---------------------------------------------------------------------------

export interface PlatformApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * Skip the 401→refresh→retry cycle. Used by the login/2fa endpoints where a
   * 401 is a real failure (bad credentials / bad code), not an expired token,
   * and where no `__sa_refresh` cookie exists yet.
   */
  skipRefresh?: boolean;
}

export async function platformApiRequest<T = unknown>(
  path: string,
  req: PlatformApiRequest = {},
): Promise<T> {
  let res = await doFetch(path, req);

  if (res.status === 401 && !req.skipRefresh) {
    const refreshed = await refreshPlatformAccessToken();
    if (refreshed) {
      res = await doFetch(path, req);
    } else {
      superAdminTokenStore.clear();
    }
  }

  return parseResponse<T>(res);
}

/**
 * Like platformApiRequest, but for binary responses (PDF). Carries the bearer
 * token + 401-refresh retry, then returns the response Blob. On a non-2xx the
 * body is parsed as JSON for the structured error message (PlatformApiError),
 * matching the JSON path. Used for the operator invoice PDF (the platform
 * routes are bearer-gated, so window.open can't carry the auth).
 */
export async function platformApiBlob(
  path: string,
  req: PlatformApiRequest = {},
): Promise<Blob> {
  let res = await doFetch(path, req);
  if (res.status === 401 && !req.skipRefresh) {
    const refreshed = await refreshPlatformAccessToken();
    if (refreshed) {
      res = await doFetch(path, req);
    } else {
      superAdminTokenStore.clear();
    }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const message =
      (body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText) || `HTTP ${res.status}`;
    throw new PlatformApiError(res.status, message, body);
  }
  return res.blob();
}

async function doFetch(path: string, req: PlatformApiRequest): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(req.headers ?? {}),
  };
  if (req.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const token = superAdminTokenStore.get();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(`${API_BASE_URL}${path}`, {
    method: req.method ?? 'GET',
    headers,
    credentials: 'include', // sends the __sa_refresh cookie
    ...(req.body !== undefined ? { body: JSON.stringify(req.body) } : {}),
  });
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (res.status === 204) {
    return undefined as T;
  }
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message =
      (isJson && body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText) || `HTTP ${res.status}`;
    throw new PlatformApiError(res.status, message, body);
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Refresh singleton — coalesces concurrent 401s into one refresh call
// ---------------------------------------------------------------------------

let inFlightRefresh: Promise<boolean> | null = null;

export async function refreshPlatformAccessToken(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}${PLATFORM_REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken?: string };
      if (!body.accessToken) return false;
      superAdminTokenStore.set(body.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

/**
 * Logout: best-effort server-side revocation of the refresh-token family
 * (POST /v1/platform/auth/logout — clears the __sa_refresh cookie + revokes the
 * SuperAdminRefreshToken rows), then clear the in-memory access token.
 *
 * The network call is intentionally a raw fetch, NOT platformApiRequest: logout
 * has different semantics (no bearer needed, no 401-refresh retry, no JSON
 * handling) and must never throw — the local token clear is the source of truth
 * for this tab, so a network failure must not block sign-out.
 */
export async function platformLogout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/v1/platform/auth/logout`, {
      method: 'POST',
      credentials: 'include', // sends the __sa_refresh cookie
    });
  } catch {
    // Network error / server down — local cleanup below still signs the user out.
  }
  superAdminTokenStore.clear();
}

/** Test-only: reset module state between tests. */
export function __resetPlatformAuthForTests(): void {
  superAdminAccessToken = null;
  inFlightRefresh = null;
}
